/**
 * Typing Indicator Component
 * Shows when Hajra is preparing a response
 *
 * Accessibility:
 * - Screen reader announcement via aria-live
 * - Respects reduced motion preferences
 * - Hidden decorative elements from assistive tech
 */

import React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../../../lib/utils';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface TypingIndicatorProps {
  className?: string;
  chatbotName?: string;
}

/**
 * Animated typing indicator with three pulsing dots
 * Shows when the AI is generating a response
 */
export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  className,
  chatbotName = 'Hajra'
}) => {
  const { prefersReducedMotion, tween } = useReducedMotion();

  // Dot animation configuration
  const getDotAnimation = (delay: number) => {
    if (prefersReducedMotion) {
      return {
        initial: { opacity: 0.6 },
        animate: { opacity: 0.6 },
        transition: { duration: 0 }
      };
    }
    return {
      initial: { y: 0, opacity: 0.4 },
      animate: {
        y: [-1, 1, -1],
        opacity: [0.4, 1, 0.4]
      },
      transition: {
        duration: 0.8,
        repeat: Infinity,
        delay,
        ease: 'easeInOut' as const
      }
    };
  };

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
      transition={tween}
      className={cn(
        'flex items-start gap-3 mb-5',
        className
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Screen reader announcement */}
      <span className="sr-only">{chatbotName} is typing a response</span>

      {/* Message bubble */}
      <motion.div
        initial={prefersReducedMotion ? {} : { scale: 0.95 }}
        animate={{ scale: 1 }}
        transition={tween}
        className={cn(
          'bg-white border border-gray-200/80',
          'rounded-2xl px-4 py-3.5',
          'shadow-xs'
        )}
        aria-hidden="true"
      >
        {/* Visual dots */}
        <div className="flex items-center gap-1.5">
          {[0, 0.15, 0.3].map((delay, i) => {
            const animation = getDotAnimation(delay);
            return (
              <motion.span
                key={i}
                initial={animation.initial}
                animate={animation.animate}
                transition={animation.transition}
                className="w-2 h-2 rounded-full bg-emerald-500"
              />
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TypingIndicator;
